import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';

/**
 * Dev Mode
 *
 * A tool for people with the Appwrite `dev` label. When dev mode is enabled a
 * floating menu appears (bottom-right) exposing dev-only features. The first
 * feature is on-screen PII obfuscation so the app can be screenshotted with
 * real vehicles without exposing customers' personal information. More features
 * can hang off this context / menu later.
 *
 * Two levels of state:
 *  - `enabled`   — dev mode itself (shows the floating menu). Toggled from the
 *                  sidebar user menu.
 *  - `obfuscate` — the PII-obfuscation feature, toggled inside the dev menu.
 *
 * `active` (obfuscation is actually in effect) = isDev && enabled && obfuscate.
 *
 * NOTE: this is unrelated to `services/devAuth` — that is a localhost-only
 * auto-login helper. This context is about what a signed-in dev sees.
 */

const STORAGE_ENABLED = 'pg-dev-mode-enabled';
const STORAGE_OBFUSCATE = 'pg-dev-mode-obfuscate';

const readBool = (key) => {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const DevModeContext = createContext(null);

export function DevModeProvider({ children }) {
  const { user } = useAuth();

  // Only users carrying the `dev` label can access dev mode at all. This label
  // is assigned via the Appwrite console (the in-app role editor is limited to
  // the standard role labels).
  const isDev = Array.isArray(user?.labels) && user.labels.includes('dev');

  const [enabled, setEnabled] = useState(() => readBool(STORAGE_ENABLED));
  const [obfuscate, setObfuscate] = useState(() => readBool(STORAGE_OBFUSCATE));

  // Images the user has chosen to hide (by image id). Kept here so the same
  // image stays hidden wherever it appears.
  const [hiddenPhotos, setHiddenPhotos] = useState(() => new Set());

  // The quote request currently open, if any. Pages register themselves via
  // useDevQuoteRequest so the dev menu can offer actions scoped to what the user
  // is looking at — the ML reset lives here rather than in the page header
  // because wiping detection results is a testing tool, not day-to-day work.
  const [quoteRequestContext, setQuoteRequestContext] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_ENABLED, enabled ? 'true' : 'false');
    } catch {
      // ignore storage failures (private mode, etc.)
    }
  }, [enabled]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_OBFUSCATE, obfuscate ? 'true' : 'false');
    } catch {
      // ignore storage failures
    }
  }, [obfuscate]);

  // Dev mode can never be "on" for a non-dev user, regardless of stored state.
  const devMenuVisible = isDev && enabled;
  // Obfuscation is in effect only when dev mode is on AND the feature is toggled on.
  const active = devMenuVisible && obfuscate;

  const toggle = useCallback(() => setEnabled((prev) => !prev), []);
  const toggleObfuscate = useCallback(() => setObfuscate((prev) => !prev), []);

  const togglePhoto = useCallback((id) => {
    if (id == null) return;
    setHiddenPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const isPhotoHidden = useCallback((id) => hiddenPhotos.has(id), [hiddenPhotos]);

  const value = useMemo(
    () => ({
      isDev,
      enabled,
      obfuscate,
      active,
      devMenuVisible,
      setEnabled,
      toggle,
      setObfuscate,
      toggleObfuscate,
      hiddenPhotos,
      togglePhoto,
      isPhotoHidden,
      quoteRequestContext,
      setQuoteRequestContext,
    }),
    [
      isDev,
      enabled,
      obfuscate,
      active,
      devMenuVisible,
      toggle,
      toggleObfuscate,
      hiddenPhotos,
      togglePhoto,
      isPhotoHidden,
      quoteRequestContext,
    ],
  );

  return <DevModeContext.Provider value={value}>{children}</DevModeContext.Provider>;
}

export function useDevMode() {
  const ctx = useContext(DevModeContext);
  if (!ctx) {
    throw new Error('useDevMode must be used within a DevModeProvider');
  }
  return ctx;
}

/**
 * Tell the dev menu which quote request is on screen, so it can offer dev
 * actions scoped to it. Registers on mount and clears on unmount.
 */
export function useDevQuoteRequest(quoteRequestId, organisationId) {
  const { setQuoteRequestContext } = useDevMode();

  useEffect(() => {
    if (!quoteRequestId) return undefined;
    setQuoteRequestContext({ quoteRequestId, organisationId: organisationId || null });
    return () => setQuoteRequestContext(null);
  }, [quoteRequestId, organisationId, setQuoteRequestContext]);
}
